import { connect, ResolveThunks } from "react-redux";
import { useMatch, useNavigate } from "react-router";
import { useGoBack } from "shared/navigation";
import { BackIcon, ICON_STROKE } from "shared/icons";
import { RootState } from "state/rootReducer";
import { actions as userActions } from "state/slices/userSlice";
import { REDIRECT_URL } from "app/routes";
import NavTabs from "layout/NavTabs";
// Spotify's guidelines require the green (or white) mark on dark grounds.
import spotifyLogo from "layout/SpotifyBar/assets/spotify-logo.png";
import vLogo from "layout/SpotifyBar/assets/v-logo-new.png";

interface OwnProps {
  loginUrl: string;
  url: string;
}

interface StateProps {
  spotifyUser: SpotifyUser | null;
}

interface DispatchProps {
  onLogout: typeof userActions.logout;
}

export type AppHeaderProps = OwnProps &
  StateProps &
  ResolveThunks<DispatchProps>;

/**
 * The app bar: identity, navigation and account.
 *
 * Previously this was a 108px band holding a 92px logo and little else, with
 * the player crammed in beside it. The player now lives in its own bar at the
 * bottom, which lets this collapse to a single compact row.
 */
export function AppHeader(props: AppHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const isCallback = window.location.href.includes(REDIRECT_URL);
  const isSongPage = useMatch("/songs/:gid") !== null;
  const goBack = useGoBack();

  return (
    <header className="flex-none flex flex-row items-center gap-2 sm:gap-4 h-14 px-3 sm:px-4 bg-surface border-b border-rule">
      {/* On phones, a song page puts Back here in place of the logo, rather
          than in a row of its own above the song. From sm the page shows its
          own Back button. */}
      {isSongPage && (
        <button
          type="button"
          className="sm:hidden button-small flex-none flex items-center justify-center w-[34px] h-[34px]"
          onClick={goBack}
          aria-label="Back"
          title="Back"
        >
          <BackIcon size={20} strokeWidth={ICON_STROKE} aria-hidden="true" />
        </button>
      )}
      {/* The navy mark needs a light plate to hold contrast on a dark ground. */}
      <img
        className={`rounded bg-[#e9e4f5] p-1 flex-none ${
          isSongPage ? "hidden sm:block" : ""
        }`}
        src={vLogo}
        alt="Vybe"
        width="34"
        height="34"
      />

      <NavTabs url={props.url} />

      <div className="ml-auto flex items-center gap-2 min-w-0">
        {props.spotifyUser === null ? (
          !isCallback && (
            <button
              className="button-generic text-sm whitespace-nowrap"
              onClick={() => window.location.replace(props.loginUrl)}
            >
              Login to Spotify
            </button>
          )
        ) : (
          <div className="flex flex-row items-center gap-2 bordered pl-1.5 pr-1 py-1 min-w-0">
            <img
              className="w-[22px] h-[22px] flex-none"
              src={spotifyLogo}
              alt="Spotify"
              width="22"
              height="22"
            />
            <span className="text-sm truncate max-w-[10rem]">
              {props.spotifyUser.display_name}
            </span>
            <button
              type="button"
              className="button-small px-1.5 text-base flex-none"
              onClick={() => props.onLogout(navigate)}
              aria-label="Log out"
              title="Log out"
            >
              ⎋
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function mapState(state: RootState): StateProps {
  return {
    spotifyUser: state.user.currentUser,
  };
}

const mapDispatch: DispatchProps = {
  onLogout: userActions.logout,
};

export default connect(mapState, mapDispatch)(AppHeader);
